import React, { useState } from "react";
import { Link } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import { api, setToken } from "@/lib/api";
import { ArrowLeft, ArrowRight, Loader2, PlayCircle, ShieldCheck, Sparkles } from "lucide-react";

/* /demo — investor entry point. One click drops the visitor into the REAL
 * product signed in as "Sarah Chen", caring for her mother Margaret, with a
 * guided tour (DemoGuide) explaining every section and who it benefits. */
export default function InvestorDemo() {
    const [busy, setBusy] = useState(false);
    const [entered, setEntered] = useState(false);

    const enter = async () => {
        setBusy(true);
        try {
            const r = await api.post("/demo/enter");
            setToken(r.data.token);
            localStorage.setItem("mlcn_session_id", r.data.user.primary_session_id);
            localStorage.setItem("ml_demo_mode", "1");
            localStorage.setItem("ml_internal_optout", "1");
            localStorage.setItem("ml_demo_step", "0");
            setEntered(true);
            // Auto-attempt after the auth-state remount settles; the anchor
            // below is the un-cancellable fallback (a real link navigation).
            setTimeout(() => { try { window.location.assign("/dashboard"); } catch { /* anchor fallback */ } }, 600);
        } catch {
            setBusy(false);
        }
    };

    const stops = [
        ["Today's dashboard", "Sarah's calm daily home base"],
        ["The Roadmap", "What's coming next, and how to prepare"],
        ["Patient profile", "The caregiver-owned record"],
        ["Care records", "AI reads documents into structured facts"],
        ["Care summary", "One secure link for any provider"],
        ["Access control", "Grant, limit, or revoke access — fully audited"],
        ["Medicare GUIDE", "Eligibility for CMS's funded dementia program"],
        ["Lane", "The 24/7 care companion"],
        ["Caregiver wellbeing", "Burnout checks & wellness support"],
        ["Learn", "Stage-matched education, week by week"],
        ["Resources", "Vetted help, matched to the family's stage"],
        ["Caregiver tools", "The free '30 Days' growth engine"],
        ["Organization view", "How networks & clinics plug in"],
    ];

    return (
        <main className="min-h-screen bg-cream">
            <SeoHead title="Live Product Demo — Memory Lane" description="An interactive, self-guided demo of the Memory Lane care platform with a fictional family." canonicalPath="/investor-demo" />
            <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 md:py-16">
                <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-medium text-[#4C5551] hover:text-teal-dark mb-8" data-testid="demo-back">
                    <ArrowLeft size={15} /> Memory Lane home
                </Link>
                <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark mb-2 flex items-center gap-1.5"><Sparkles size={13} /> Interactive investor demo</p>
                <h1 className="text-4xl sm:text-5xl font-heading font-semibold tracking-tight text-[#12776F]" data-testid="demo-title">
                    Meet Sarah — and see the whole platform through her eyes.
                </h1>
                <p className="mt-4 text-base md:text-lg text-[#4C5551] leading-relaxed max-w-2xl">
                    Sarah cares for her mother, Margaret &ldquo;Maggie&rdquo; Chen, who is living with mid-stage dementia.
                    One click signs you into the real product with their (entirely fictional) story pre-loaded —
                    medications, documents, care team, timeline, and conversations. A guide follows you through
                    each section explaining what it is and who it benefits: the caregiver, the clinician, the
                    organization, and ultimately the person living with dementia.
                </p>
                <div className="mt-7 flex flex-wrap items-center gap-4">
                    {entered ? (
                        <a href="/dashboard" className="ml-btn-primary !px-7" data-testid="demo-start-tour">
                            You're in — start the tour <ArrowRight size={17} />
                        </a>
                    ) : (
                        <button onClick={enter} disabled={busy} className="ml-btn-primary !px-7" data-testid="demo-enter">
                            {busy ? <Loader2 size={17} className="animate-spin" /> : <PlayCircle size={18} />} Enter the live demo
                        </button>
                    )}
                    <span className="text-sm text-[#6E7A74] inline-flex items-center gap-1.5"><ShieldCheck size={14} /> No signup · all data fictional · resets hourly</span>
                </div>
                <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {stops.map(([t, d], i) => (
                        <div key={t} className="ml-card p-4 flex items-start gap-3" data-testid={`demo-stop-${i}`}>
                            <span className="w-7 h-7 rounded-full bg-teal-dark text-white text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                            <div>
                                <p className="text-sm font-semibold text-[#1F2624]">{t}</p>
                                <p className="text-xs text-[#6E7A74] mt-0.5">{d}</p>
                            </div>
                        </div>
                    ))}
                    <div className="ml-card p-4 flex items-center gap-2 border-teal/40 bg-teal-soft/30">
                        <ArrowRight size={16} className="text-teal-dark" />
                        <p className="text-sm text-[#2A3330]">…ending with where the platform goes next.</p>
                    </div>
                </div>
            </div>
        </main>
    );
}
